import React, { createContext, useState } from 'react';

export enum Step {
  FORM,
  THANK_YOU,
}

export enum Reason {
  NOT_HELPFUL = 'NOT_HELPFUL',
  INACCURATE = 'INACCURATE',
  BIASED = 'BIASED',
  OFFENSIVE = 'OFFENSIVE',
  OTHER = 'OTHER',
}

export interface FaqReportingContextValue {
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedReason: Reason | undefined;
  setSelectedReason: React.Dispatch<React.SetStateAction<Reason | undefined>>;
  additionalInfo: string | undefined;
  setAdditionalInfo: React.Dispatch<React.SetStateAction<string | undefined>>;
  currentStep: Step;
  setCurrentStep: React.Dispatch<React.SetStateAction<Step>>;
  afterSubmit: () => void;
  closeModal: () => void;
  searchRequestObject?: TSearchRequestObject;
}

export const initialContextValue: FaqReportingContextValue = {
  modalOpen: false,
  setModalOpen: () => false,
  selectedReason: undefined,
  setSelectedReason: () => undefined,
  additionalInfo: undefined,
  setAdditionalInfo: () => undefined,
  currentStep: Step.FORM,
  setCurrentStep: () => Step.FORM,
  afterSubmit: () => {},
  closeModal: () => {},
  searchRequestObject: undefined,
};

export const FaqReportingContext = createContext<FaqReportingContextValue>(initialContextValue);

interface FaqReportingProviderProps {
  children: React.ReactNode;
  searchRequestObject?: TSearchRequestObject;
}

export const FaqReportingProvider: React.FC<FaqReportingProviderProps> = ({ children, searchRequestObject }) => {
  const [selectedReason, setSelectedReason] = useState<Reason | undefined>(undefined);
  const [additionalInfo, setAdditionalInfo] = useState<string | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<Step>(Step.FORM);

  const afterSubmit = () => {
    setSelectedReason(undefined);
    setAdditionalInfo(undefined);
    setCurrentStep(Step.THANK_YOU);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentStep(Step.FORM);
  };

  const contextValue: FaqReportingContextValue = {
    modalOpen,
    setModalOpen,
    selectedReason,
    setSelectedReason,
    additionalInfo,
    setAdditionalInfo,
    currentStep,
    setCurrentStep,
    afterSubmit,
    closeModal,
    searchRequestObject,
  };

  return <FaqReportingContext.Provider value={contextValue}>{children}</FaqReportingContext.Provider>;
};
